// ---- Furniture registry ----
// One import surface for all interior props. `common.ts` holds theme-neutral
// pieces any building can use; `heraldic.ts` holds the castle/great-hall set.
// Add a generic piece to common.ts, a royal one to heraldic.ts — both flow
// through here, so structures just `import { … } from "../furniture/index.ts"`.

export * from "./common.ts";
export * from "./heraldic.ts";

import type { StructureBuildCtx } from "../define.ts";
import {
  rug, bench, stool, bookshelf, chandelier, barrel, crate, chest,
  roundTable, bed, fireplace, shopCounter, shopSign,
} from "./common.ts";
import { throne, banquetTable, fourPosterBed } from "./heraldic.ts";

import { type FurniturePiece } from "./pieces.ts";
export { FURNITURE_PIECES, type FurniturePiece } from "./pieces.ts";

/** Uniform NAME → placer adapters over the piece functions, so data-driven
 *  structures (content/structures — src/structure/doc.ts `furnish` op) can
 *  place furniture by name. Keyed by FurniturePiece (pieces.ts — the
 *  dependency-free name list validation also checks), so registry and
 *  validator can't drift. `args` carries the piece-specific knobs (y0,
 *  length, w, d, s, faceX, ceilingY, nx, nz) with sensible defaults; pieces
 *  whose signatures don't map to numbers (tapestry/wallCrest) stay code-only —
 *  the `part` escape hatch covers one-offs. The `?? …` defaults here mirror
 *  pieces.ts's PIECE_ARGS table (the studio Builder's arg picker) — change a
 *  default in both places. */
export const FURNITURE: Record<
  FurniturePiece,
  (c: StructureBuildCtx, x: number, z: number, args: Record<string, number | string>) => void
> = {
  rug: (c, x, z, a) => rug(c, x, z, a.w ?? 3, a.d ?? 2),
  bench: (c, x, z, a) => bench(c, x, z, a.length ?? 2.2, a.face ?? Math.PI / 2),
  stool: (c, x, z, a) => stool(c, x, z, a.y0 ?? 0, a.face ?? 0),
  bookshelf: (c, x, z, a) => bookshelf(c, x, z, (a.faceX ?? 1) >= 0 ? 1 : -1, a.y0 ?? 0),
  chandelier: (c, x, z, a) => chandelier(c, x, z, a.ceilingY ?? 3),
  barrel: (c, x, z) => barrel(c, x, z),
  crate: (c, x, z, a) => crate(c, x, z, a.s ?? 0.9),
  chest: (c, x, z, a) => chest(c, x, z, a.y0 ?? 0),
  roundTable: (c, x, z, a) => roundTable(c, x, z, a.y0 ?? 0),
  bed: (c, x, z, a) => bed(c, x, z, a.y0 ?? 0),
  fireplace: (c, x, z, a) =>
    fireplace(
      c, x, z,
      ((a.nx ?? 0) >= 1 ? 1 : (a.nx ?? 0) <= -1 ? -1 : 0),
      ((a.nz ?? -1) >= 1 ? 1 : (a.nz ?? -1) <= -1 ? -1 : 0),
      a.y0 ?? 0,
    ),
  throne: (c, x, z) => throne(c, x, z),
  banquetTable: (c, x, z, a) => banquetTable(c, x, z, a.length ?? 6),
  fourPosterBed: (c, x, z, a) => fourPosterBed(c, x, z, a.y0 ?? 0),
  shopCounter: (c, x, z, a) => shopCounter(c, x, z, Number(a.length ?? 4.2)),
  shopSign: (c, x, z, a) => {
    const nxN = Number(a.nx ?? -1);
    const nzN = Number(a.nz ?? 0);
    shopSign(
      c, x, z,
      (nxN >= 1 ? 1 : nxN <= -1 ? -1 : 0),
      (nzN >= 1 ? 1 : nzN <= -1 ? -1 : 0),
      typeof a.line1 === "string" && a.line1.length ? a.line1 : "General",
      typeof a.line2 === "string" ? a.line2 : "Store",
    );
  },
};
